import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, ArrowLeft, Plus, Trash2, ChevronDown, ChevronUp, Upload, Video, Check, X, HelpCircle, Loader2, FileText, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { adminApi, quizApi } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';

interface Answer {
  id?: string;
  answerEn: string;
  isCorrect: boolean;
  orderIndex: number;
}

interface Question {
  id?: string;
  questionEn: string;
  orderIndex: number;
  answers: Answer[];
}

interface Lesson {
  id?: string;
  titleEn: string;
  contentType: 'video' | 'text' | 'quiz';
  videoKey?: string;
  transcodeStatus?: string;
  videoDuration?: number;
  textContent?: string;
  htmlFileKey?: string;
  orderIndex: number;
  isFree: boolean;
  questions?: Question[];
}

interface Module {
  id?: string;
  titleEn: string;
  orderIndex: number;
  lessons: Lesson[];
  isExpanded?: boolean;
}

export default function AdminCourseEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === 'new';

  const [formData, setFormData] = useState({
    titleEn: '', slug: '', shortDescriptionEn: '', descriptionEn: '',
    price: 0, currency: 'IQD', durationHours: 0, level: 'beginner',
    language: 'en', instructorName: '', instructorBio: '',
    isPublished: false, isFeatured: false, accessMode: 'public', allowedEmails: '[]',
  });

  const [modules, setModules] = useState<Module[]>([]);
  const [uploadingLesson, setUploadingLesson] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [transcodingLesson, setTranscodingLesson] = useState<Record<string, string>>({});
  const [transcodeProgress, setTranscodeProgress] = useState<Record<string, number>>({});
  const [editingQuiz, setEditingQuiz] = useState<string | null>(null);
  const [quizQuestions, setQuizQuestions] = useState<Question[]>([]);
  const [savedQuizCounts, setSavedQuizCounts] = useState<Record<string, number>>({});
  const [quizPassMark, setQuizPassMark] = useState(70);

  const { data: course, isLoading } = useQuery({
    queryKey: ['admin-course', id],
    queryFn: () => adminApi.getCourse(id!),
    enabled: !isNew && !!id,
  });

  const { data: courseModules } = useQuery({
    queryKey: ['admin-course-modules', id],
    queryFn: () => adminApi.getModules(id!),
    enabled: !isNew && !!id,
  });

  useEffect(() => {
    if (course) {
      setFormData({
        titleEn: course.titleEn || '', slug: course.slug || '',
        shortDescriptionEn: course.shortDescriptionEn || '',
        descriptionEn: course.descriptionEn || '',
        price: course.price || 0, currency: course.currency || 'IQD',
        durationHours: course.durationHours || 0, level: course.level || 'beginner',
        language: course.language || 'en', instructorName: course.instructorName || '',
        thumbnailUrl: course.thumbnailUrl || "", instructorBio: course.instructorBio || '',
        isPublished: course.isPublished || false, isFeatured: course.isFeatured || false,
        accessMode: course.accessMode || 'public', allowedEmails: course.allowedEmails || '[]',
      });
    }
  }, [course]);

  useEffect(() => {
    if (courseModules) {
      setModules(courseModules.map((m: any) => ({ ...m, isExpanded: true })));
    }
  }, [courseModules]);

  const saveMutation = useMutation({
    mutationFn: (data: any) => isNew ? adminApi.createCourse(data) : adminApi.updateCourse(id!, data),
    onSuccess: (response) => {
      toast({ title: 'Success', description: 'Course saved!' });
      queryClient.invalidateQueries({ queryKey: ['admin-courses'] });
      if (isNew && response?.id) navigate(`/admin/courses/${response.id}`);
    },
    onError: (error: any) => toast({ title: 'Error', description: error.message, variant: 'destructive' }),
  });

  const saveModuleMutation = useMutation({
    mutationFn: (data: any) => data.id ? adminApi.updateModule(data.id, data) : adminApi.createModule({ ...data, courseId: id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-course-modules', id] });
      toast({ title: 'Success', description: 'Module saved!' });
    },
    onError: (error: any) => toast({ title: 'Error', description: error.message, variant: 'destructive' }),
  });

  const saveLessonMutation = useMutation({
    mutationFn: (data: any) => data.id ? adminApi.updateLesson(data.id, data) : adminApi.createLesson(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-course-modules', id] });
      toast({ title: 'Success', description: 'Lesson saved!' });
    },
    onError: (error: any) => toast({ title: 'Error', description: error.message, variant: 'destructive' }),
  });

  const deleteModuleMutation = useMutation({
    mutationFn: (moduleId: string) => adminApi.deleteModule(moduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-course-modules', id] });
      toast({ title: 'Success', description: 'Module deleted!' });
    },
  });

  const deleteLessonMutation = useMutation({
    mutationFn: (lessonId: string) => adminApi.deleteLesson(lessonId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-course-modules', id] });
      toast({ title: 'Success', description: 'Lesson deleted!' });
    },
  });

  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); saveMutation.mutate(formData); };

  const generateSlug = () => {
    const slug = formData.titleEn.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    setFormData({ ...formData, slug });
  };

  const addModule = () => {
    const newModule: Module = { titleEn: 'New Module', orderIndex: modules.length, lessons: [], isExpanded: true };
    setModules([...modules, newModule]);
  };

  const addLesson = (moduleIndex: number) => {
    const newLesson: Lesson = { titleEn: 'New Lesson', contentType: 'video', orderIndex: modules[moduleIndex].lessons.length, isFree: false };
    const updated = [...modules];
    updated[moduleIndex].lessons.push(newLesson);
    setModules(updated);
  };

  const updateModule = (index: number, field: string, value: any) => {
    const updated = [...modules];
    (updated[index] as any)[field] = value;
    setModules(updated);
  };

  const updateLesson = (moduleIndex: number, lessonIndex: number, field: string, value: any) => {
    const updated = [...modules];
    (updated[moduleIndex].lessons[lessonIndex] as any)[field] = value;
    setModules(updated);
  };

  const saveModule = (moduleIndex: number) => {
    const module = modules[moduleIndex];
    saveModuleMutation.mutate({ id: module.id, titleEn: module.titleEn, orderIndex: module.orderIndex, courseId: id });
  };

  const saveLesson = (moduleIndex: number, lessonIndex: number) => {
    const module = modules[moduleIndex];
    const lesson = module.lessons[lessonIndex];
    saveLessonMutation.mutate({
      id: lesson.id, moduleId: module.id, titleEn: lesson.titleEn, contentType: lesson.contentType,
      videoKey: lesson.videoKey, videoDuration: lesson.videoDuration, textContent: lesson.textContent, htmlFileKey: lesson.htmlFileKey,
      orderIndex: lesson.orderIndex, isFree: lesson.isFree
    });
  };

  const handleVideoUpload = async (moduleIndex: number, lessonIndex: number, file: File) => {
    const lessonKey = `${moduleIndex}-${lessonIndex}`;
    const lesson = modules[moduleIndex].lessons[lessonIndex];

    if (!lesson.id) {
      toast({ title: 'Error', description: 'Please save the lesson first before uploading video', variant: 'destructive' });
      return;
    }

    // File validation
    const maxSize = 2 * 1024 * 1024 * 1024; // 2GB
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska', 'video/webm'];
    if (file.size > maxSize) {
      toast({ title: 'Error', description: 'File too large. Maximum size is 2GB.', variant: 'destructive' });
      return;
    }
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(mp4|mov|avi|mkv|webm)$/i)) {
      toast({ title: 'Error', description: 'Invalid file type. Use MP4, MOV, AVI, MKV, or WebM.', variant: 'destructive' });
      return;
    }

    setUploadingLesson(lessonKey);
    setUploadProgress(prev => ({ ...prev, [lessonKey]: 0 }));

    try {
      // Step 1: Get presigned URL
      const { uploadUrl, videoKey } = await adminApi.getUploadUrl(lesson.id, file.type, file.name);

      // Step 2: Upload with progress tracking using XMLHttpRequest
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percent = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(prev => ({ ...prev, [lessonKey]: percent }));
          }
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed with status ${xhr.status}`));
        });
        xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
        xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      // Step 3: Save videoKey to database
      await fetch(`/api/admin/lessons/${lesson.id}/video-key`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoKey }),
      });

      // Step 4: Update local state
      updateLesson(moduleIndex, lessonIndex, 'videoKey', videoKey);
      updateLesson(moduleIndex, lessonIndex, 'transcodeStatus', 'not_started');
      toast({ title: 'Success', description: 'Video uploaded successfully!', duration: 5000 });
    } catch (error: any) {
      toast({ title: 'Upload Failed', description: error.message, variant: 'destructive' });
    } finally {
      setUploadingLesson(null);
      setUploadProgress(prev => { const n = { ...prev }; delete n[lessonKey]; return n; });
    }
  };

  // Handle video transcoding
  const handleTranscode = async (moduleIndex: number, lessonIndex: number) => {
    const lessonKey = `${moduleIndex}-${lessonIndex}`;
    const lesson = modules[moduleIndex].lessons[lessonIndex];

    if (!lesson.id || !lesson.videoKey) {
      toast({ title: 'Error', description: 'No video to transcode', variant: 'destructive' });
      return;
    }

    try {
      setTranscodingLesson(prev => ({ ...prev, [lessonKey]: 'starting' }));
      toast({ title: 'Transcoding', description: 'Starting video transcoding...' });

      const res = await fetch(`/api/admin/lessons/${lesson.id}/transcode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();

      if (!data.success) throw new Error(data.error || 'Failed to start transcoding');

      const jobId = data.data.jobId;
      setTranscodingLesson(prev => ({ ...prev, [lessonKey]: jobId }));
      updateLesson(moduleIndex, lessonIndex, 'transcodeStatus', 'processing');

      // Poll for status every 10 seconds
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await fetch(`/api/admin/transcode/${jobId}?lessonId=${lesson.id}`);
          const statusData = await statusRes.json();

          if (statusData.success) {
            const { status, percentComplete } = statusData.data;
            setTranscodeProgress(prev => ({ ...prev, [lessonKey]: percentComplete || 0 }));

            if (status === 'COMPLETE') {
              clearInterval(pollInterval);
              updateLesson(moduleIndex, lessonIndex, 'transcodeStatus', 'complete');
              setTranscodingLesson(prev => { const n = { ...prev }; delete n[lessonKey]; return n; });
              setTranscodeProgress(prev => { const n = { ...prev }; delete n[lessonKey]; return n; });
              toast({ title: 'Success', description: 'Video transcoded and ready to stream!', duration: 5000 });
            } else if (status === 'ERROR' || status === 'CANCELED') {
              clearInterval(pollInterval);
              updateLesson(moduleIndex, lessonIndex, 'transcodeStatus', 'failed');
              setTranscodingLesson(prev => { const n = { ...prev }; delete n[lessonKey]; return n; });
              toast({ title: 'Transcode Failed', description: statusData.data.errorMessage || 'Transcoding failed', variant: 'destructive' });
            }
          }
        } catch (err) {
          console.error('Polling error:', err);
        }
      }, 10000);
    } catch (error: any) {
      setTranscodingLesson(prev => { const n = { ...prev }; delete n[lessonKey]; return n; });
      updateLesson(moduleIndex, lessonIndex, 'transcodeStatus', 'failed');
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  // Handle video removal
  const handleRemoveVideo = async (moduleIndex: number, lessonIndex: number) => {
    const lesson = modules[moduleIndex].lessons[lessonIndex];
    if (!lesson.id) return;

    if (!window.confirm('This will remove the video for all students. Are you sure?')) return;

    try {
      const res = await fetch(`/api/admin/lessons/${lesson.id}/video`, { method: 'DELETE' });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);

      updateLesson(moduleIndex, lessonIndex, 'videoKey', undefined);
      updateLesson(moduleIndex, lessonIndex, 'transcodeStatus', 'not_started');
      toast({ title: 'Removed', description: 'Video removed successfully.' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleFileUpload = async (moduleIndex: number, lessonIndex: number, file: File, field: 'textContent' | 'htmlFileKey', suffix: string) => {
    const lesson = modules[moduleIndex].lessons[lessonIndex];
    if (!lesson.id) return;
    const key = `${moduleIndex}-${lessonIndex}-${suffix}`;
    try {
      setUploadingLesson(key);
      setUploadProgress(prev => ({ ...prev, [key]: 0 }));
      const urlRes = await fetch(`/api/admin/lessons/${lesson.id}/file-upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contentType: file.type, filename: file.name.replace(/[^a-zA-Z0-9.-]/g, '_') }),
      });
      const urlData = await urlRes.json();
      if (!urlData.success) throw new Error(urlData.error);
      const { uploadUrl, fileKey } = urlData.data;
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(prev => ({ ...prev, [key]: pct }));
          }
        });
        xhr.addEventListener('load', () => xhr.status === 200 ? resolve() : reject(new Error('Upload failed')));
        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });
      const saveRes = await fetch(`/api/admin/lessons/${lesson.id}/file-key`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileKey, field }),
      });
      const saveData = await saveRes.json();
      if (!saveData.success) throw new Error(saveData.error);
      updateLesson(moduleIndex, lessonIndex, field, fileKey);
      toast({ title: 'Success', description: `${suffix.toUpperCase()} file uploaded successfully.` });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setUploadingLesson(null);
    }
  };

  const handleRemoveFile = async (moduleIndex: number, lessonIndex: number, field: 'textContent' | 'htmlFileKey') => {
    const lesson = modules[moduleIndex].lessons[lessonIndex];
    if (!lesson.id) return;
    if (!window.confirm('Remove this file?')) return;
    try {
      const res = await fetch(`/api/admin/lessons/${lesson.id}/file?field=${field}`, { method: 'DELETE' });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      updateLesson(moduleIndex, lessonIndex, field, undefined);
      toast({ title: 'Removed', description: 'File removed successfully.' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

    const toggleModule = (index: number) => {
    const updated = [...modules];
    updated[index].isExpanded = !updated[index].isExpanded;
    setModules(updated);
  };

  const moveModule = (index: number, direction: 'up' | 'down') => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === modules.length - 1)) return;
    const updated = [...modules];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    updated.forEach((m, i) => m.orderIndex = i);
    setModules(updated);
  };

  const moveLesson = (moduleIndex: number, lessonIndex: number, direction: 'up' | 'down') => {
    const lessons = modules[moduleIndex].lessons;
    if ((direction === 'up' && lessonIndex === 0) || (direction === 'down' && lessonIndex === lessons.length - 1)) return;
    const updated = [...modules];
    const newIndex = direction === 'up' ? lessonIndex - 1 : lessonIndex + 1;
    [updated[moduleIndex].lessons[lessonIndex], updated[moduleIndex].lessons[newIndex]] = 
      [updated[moduleIndex].lessons[newIndex], updated[moduleIndex].lessons[lessonIndex]];
    updated[moduleIndex].lessons.forEach((l, i) => l.orderIndex = i);
    setModules(updated);
  };

  // Quiz Management
  const openQuizEditor = async (lessonId: string) => {
    setEditingQuiz(lessonId);
    try {
      const questions = await quizApi.getQuestions(lessonId);
      setQuizQuestions(questions || []);
    } catch { 
      setQuizQuestions([]); 
    }
  };

  const addQuestion = () => {
    setQuizQuestions([...quizQuestions, { questionEn: '', orderIndex: quizQuestions.length, answers: [] }]);
  };

  const updateQuestion = (index: number, field: string, value: any) => {
    const updated = [...quizQuestions];
    (updated[index] as any)[field] = value;
    setQuizQuestions(updated);
  };

  const addAnswer = (questionIndex: number) => {
    const updated = [...quizQuestions];
    updated[questionIndex].answers.push({ answerEn: '', isCorrect: false, orderIndex: updated[questionIndex].answers.length });
    setQuizQuestions(updated);
  };

  const updateAnswer = (questionIndex: number, answerIndex: number, field: string, value: any) => {
    const updated = [...quizQuestions];
    if (field === 'isCorrect' && value) {
      updated[questionIndex].answers.forEach((a, i) => a.isCorrect = i === answerIndex);
    } else {
      (updated[questionIndex].answers[answerIndex] as any)[field] = value;
    }
    setQuizQuestions(updated);
  };

  const deleteQuestion = async (index: number) => {
    const q = quizQuestions[index];
    if (q.id) await quizApi.deleteQuestion(q.id);
    setQuizQuestions(quizQuestions.filter((_, i) => i !== index));
  };

  const saveQuiz = async () => {
    if (!editingQuiz) return;
    try {
      for (const q of quizQuestions) {
        if (q.id) {
          await quizApi.updateQuestion(q.id, { questionEn: q.questionEn, orderIndex: q.orderIndex });
          // Update existing answers and create new ones
          for (const a of q.answers) {
            if (a.id) {
              await quizApi.updateAnswer(a.id, { answerEn: a.answerEn, isCorrect: a.isCorrect, orderIndex: a.orderIndex });
            } else {
              await quizApi.createAnswer(q.id, { answerEn: a.answerEn, isCorrect: a.isCorrect, orderIndex: a.orderIndex });
            }
          }
        } else {
          await quizApi.createQuestion(editingQuiz, { questionEn: q.questionEn, orderIndex: q.orderIndex, answers: q.answers });
        }
      }
      toast({ title: 'Success', description: 'Quiz saved successfully!', duration: 5000 });
      setSavedQuizCounts(prev => ({ ...prev, [editingQuiz]: quizQuestions.length }));
      setEditingQuiz(null);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  if (!isNew && isLoading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div>
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/courses')}><ArrowLeft className="h-5 w-5" /></Button>
        <h1 className="text-3xl font-bold">{isNew ? 'New Course' : 'Edit Course'}</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info Card */}
            <Card>
              <CardHeader><CardTitle>Basic Information</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Title *</label>
                  <Input value={formData.titleEn} onChange={(e) => setFormData({ ...formData, titleEn: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Slug (URL)</label>
                  <div className="flex gap-2">
                    <Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} />
                    <Button type="button" variant="outline" onClick={generateSlug}>Generate</Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Short Description *</label>
                  <textarea className="w-full px-3 py-2 border rounded-md min-h-[80px]" value={formData.shortDescriptionEn} onChange={(e) => setFormData({ ...formData, shortDescriptionEn: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Full Description *</label>
                  <textarea className="w-full px-3 py-2 border rounded-md min-h-[120px]" value={formData.descriptionEn} onChange={(e) => setFormData({ ...formData, descriptionEn: e.target.value })} required />
                </div>
              </CardContent>
            </Card>

            {/* Instructor Card */}
            <Card>
              <CardHeader><CardTitle>Instructor</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Name *</label>
                  <Input value={formData.instructorName} onChange={(e) => setFormData({ ...formData, instructorName: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Bio</label>
                  <textarea className="w-full px-3 py-2 border rounded-md min-h-[80px]" value={formData.instructorBio} onChange={(e) => setFormData({ ...formData, instructorBio: e.target.value })} />
                </div>
              </CardContent>
            </Card>

            {/* Course Content Card */}
            {!isNew && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Course Content</CardTitle>
                  <Button type="button" variant="outline" size="sm" onClick={addModule}><Plus className="h-4 w-4 me-2" /> Add Module</Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  {modules.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No modules yet. Click "Add Module" to start.</p>
                  ) : (
                    modules.map((module, mi) => (
                      <div key={module.id || mi} className="border rounded-lg">
                        {/* Module Header */}
                        <div className="flex items-center gap-2 p-3 bg-gray-50">
                          <div className="flex flex-col">
                            <button type="button" onClick={() => moveModule(mi, 'up')} className="p-0.5 hover:bg-gray-200 rounded" disabled={mi === 0}><ChevronUp className="h-3 w-3" /></button>
                            <button type="button" onClick={() => moveModule(mi, 'down')} className="p-0.5 hover:bg-gray-200 rounded" disabled={mi === modules.length - 1}><ChevronDown className="h-3 w-3" /></button>
                          </div>
                          <button type="button" onClick={() => toggleModule(mi)}>{module.isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</button>
                          <span className="text-sm font-medium text-gray-500">Module {mi + 1}</span>
                          <Input value={module.titleEn} onChange={(e) => updateModule(mi, 'titleEn', e.target.value)} className="flex-1" placeholder="Module Title" />
                          <Button type="button" variant="outline" size="sm" onClick={() => saveModule(mi)}><Save className="h-4 w-4" /></Button>
                          <Button type="button" variant="destructive" size="sm" onClick={() => module.id && deleteModuleMutation.mutate(module.id)}><Trash2 className="h-4 w-4" /></Button>
                        </div>

                        {/* Lessons */}
                        {module.isExpanded && (
                          <div className="p-3 space-y-2">
                            {module.lessons.map((lesson, li) => (
                              <div key={lesson.id || li} className="flex items-start gap-2 p-3 bg-white border rounded">
                                <div className="flex flex-col">
                                  <button type="button" onClick={() => moveLesson(mi, li, 'up')} className="p-0.5 hover:bg-gray-100 rounded" disabled={li === 0}><ChevronUp className="h-3 w-3" /></button>
                                  <button type="button" onClick={() => moveLesson(mi, li, 'down')} className="p-0.5 hover:bg-gray-100 rounded" disabled={li === module.lessons.length - 1}><ChevronDown className="h-3 w-3" /></button>
                                </div>
                                <div className="flex-1 space-y-2">
                                  <Input value={lesson.titleEn} onChange={(e) => updateLesson(mi, li, 'titleEn', e.target.value)} placeholder="Lesson Title" />
                                  {lesson.contentType === 'video' && <Input value={lesson.videoDuration ? `${Math.floor(lesson.videoDuration / 60)}:${(lesson.videoDuration % 60).toString().padStart(2, '0')}` : ''} onChange={(e) => { const [min, sec] = e.target.value.split(':').map(Number); const totalSeconds = (min || 0) * 60 + (sec || 0); updateLesson(mi, li, 'videoDuration', totalSeconds); }} placeholder="Duration (MM:SS)" className="w-32" />}
                                  <div className="flex items-center gap-3 flex-wrap">
                                    <select value={lesson.contentType} onChange={(e) => updateLesson(mi, li, 'contentType', e.target.value)} className="px-2 py-1 border rounded text-sm">
                                      <option value="video">📹 Video</option>
                                      <option value="text">📄 Text</option>
                                      <option value="quiz">❓ Quiz</option>
                                    </select>
                                    {lesson.contentType === 'video' && <label className="flex items-center gap-1 text-sm">
                                      <input type="checkbox" checked={lesson.isFree} onChange={(e) => updateLesson(mi, li, 'isFree', e.target.checked)} />
                                      Free Preview
                                    </label>}
                                    {lesson.contentType === 'video' && (
                                      <div className="flex items-center gap-2 flex-wrap">
                                        {/* State: Uploading with progress */}
                                        {uploadingLesson === `${mi}-${li}` ? (
                                          <div className="flex items-center gap-2">
                                            <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                                            <div className="w-32 bg-gray-200 rounded-full h-2">
                                              <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${uploadProgress[`${mi}-${li}`] || 0}%` }} />
                                            </div>
                                            <span className="text-xs text-blue-600">{uploadProgress[`${mi}-${li}`] || 0}%</span>
                                          </div>
                                        ) : lesson.videoKey ? (
                                          <>
                                            {/* State: Transcoding in progress */}
                                            {transcodingLesson[`${mi}-${li}`] ? (
                                              <div className="flex items-center gap-2">
                                                <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
                                                <span className="text-xs text-amber-600">Transcoding {transcodeProgress[`${mi}-${li}`] || 0}%</span>
                                              </div>
                                            ) : lesson.transcodeStatus === 'complete' ? (
                                              /* State: Ready to stream */
                                              <span className="text-green-600 text-sm flex items-center gap-1"><Check className="h-4 w-4" /> Ready to Stream</span>
                                            ) : lesson.transcodeStatus === 'failed' ? (
                                              /* State: Transcode failed */
                                              <>
                                                <span className="text-red-600 text-sm flex items-center gap-1"><X className="h-4 w-4" /> Transcode Failed</span>
                                                <button type="button" onClick={() => handleTranscode(mi, li)} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"><RotateCcw className="h-3 w-3" /> Retry</button>
                                              </>
                                            ) : (
                                              /* State: Uploaded, needs transcoding */
                                              <>
                                                <span className="text-green-600 text-sm flex items-center gap-1"><Video className="h-4 w-4" /> Uploaded</span>
                                                <button type="button" onClick={() => handleTranscode(mi, li)} className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded hover:bg-amber-200 flex items-center gap-1"><Loader2 className="h-3 w-3" /> Transcode</button>
                                              </>
                                            )}
                                            {/* Remove button (always visible when video exists and not transcoding) */}
                                            {!transcodingLesson[`${mi}-${li}`] && (
                                              <button type="button" onClick={() => handleRemoveVideo(mi, li)} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"><Trash2 className="h-3 w-3" /> Remove</button>
                                            )}
                                          </>
                                        ) : (
                                          /* State: No video - show upload button */
                                          <label className="cursor-pointer">
                                            <input type="file" accept="video/*" className="hidden" onChange={(e) => e.target.files?.[0] && handleVideoUpload(mi, li, e.target.files[0])} disabled={!lesson.id} />
                                            <span className={`flex items-center gap-1 text-sm ${lesson.id ? 'text-blue-600 hover:text-blue-800 cursor-pointer' : 'text-gray-400'}`}>
                                              <Upload className="h-4 w-4" /> Upload Video
                                            </span>
                                            {!lesson.id && <span className="text-xs text-red-500 block">Save lesson first</span>}
                                          </label>
                                        )}
                                      </div>
                                    )}
                                    {lesson.contentType === 'text' && (
                                      <div className="flex flex-col gap-2">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="text-xs font-semibold text-gray-500 w-12">PDF:</span>
                                          {uploadingLesson === `${mi}-${li}-pdf` ? (
                                            <div className="flex items-center gap-2">
                                              <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                                              <div className="w-32 bg-gray-200 rounded-full h-2">
                                                <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${uploadProgress[`${mi}-${li}-pdf`] || 0}%` }} />
                                              </div>
                                              <span className="text-xs text-blue-600">{uploadProgress[`${mi}-${li}-pdf`] || 0}%</span>
                                            </div>
                                          ) : lesson.textContent ? (
                                            <>
                                              <span className="text-green-600 text-sm flex items-center gap-1"><FileText className="h-4 w-4" /> PDF Uploaded</span>
                                              <button type="button" onClick={() => handleRemoveFile(mi, li, 'textContent')} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"><Trash2 className="h-3 w-3" /> Remove</button>
                                            </>
                                          ) : (
                                            <label className="cursor-pointer">
                                              <input type="file" accept=".pdf" className="hidden" onChange={(e) => e.target.files?.[0] && handleFileUpload(mi, li, e.target.files[0], 'textContent', 'pdf')} disabled={!lesson.id} />
                                              <span className={`flex items-center gap-1 text-sm ${lesson.id ? 'text-blue-600 hover:text-blue-800 cursor-pointer' : 'text-gray-400'}`}>
                                                <Upload className="h-4 w-4" /> Upload PDF
                                              </span>
                                              {!lesson.id && <span className="text-xs text-red-500 block">Save lesson first</span>}
                                            </label>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="text-xs font-semibold text-gray-500 w-12">HTML:</span>
                                          {uploadingLesson === `${mi}-${li}-html` ? (
                                            <div className="flex items-center gap-2">
                                              <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                                              <div className="w-32 bg-gray-200 rounded-full h-2">
                                                <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${uploadProgress[`${mi}-${li}-html`] || 0}%` }} />
                                              </div>
                                              <span className="text-xs text-blue-600">{uploadProgress[`${mi}-${li}-html`] || 0}%</span>
                                            </div>
                                          ) : lesson.htmlFileKey ? (
                                            <>
                                              <span className="text-green-600 text-sm flex items-center gap-1"><FileText className="h-4 w-4" /> HTML Uploaded</span>
                                              <button type="button" onClick={() => handleRemoveFile(mi, li, 'htmlFileKey')} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"><Trash2 className="h-3 w-3" /> Remove</button>
                                            </>
                                          ) : (
                                            <label className="cursor-pointer">
                                              <input type="file" accept=".html,.htm" className="hidden" onChange={(e) => e.target.files?.[0] && handleFileUpload(mi, li, e.target.files[0], 'htmlFileKey', 'html')} disabled={!lesson.id} />
                                              <span className={`flex items-center gap-1 text-sm ${lesson.id ? 'text-blue-600 hover:text-blue-800 cursor-pointer' : 'text-gray-400'}`}>
                                                <Upload className="h-4 w-4" /> Upload HTML
                                              </span>
                                              {!lesson.id && <span className="text-xs text-red-500 block">Save lesson first</span>}
                                            </label>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {lesson.contentType === 'quiz' && lesson.id && (<>
                                      <Button type="button" variant="outline" size="sm" onClick={() => openQuizEditor(lesson.id!)}><HelpCircle className="h-4 w-4 me-1" /> Edit Quiz</Button>
                                      {savedQuizCounts[lesson.id!] ? <span className="text-xs text-green-600 font-medium">{'✓ ' + savedQuizCounts[lesson.id!] + ' questions saved'}</span> : null}
                                    </>)}
                                    {lesson.contentType === 'quiz' && !lesson.id && (
                                      <span className="text-xs text-red-500">Save lesson first</span>
                                    )}
                                  </div>
                                </div>
                                <Button type="button" variant="outline" size="sm" onClick={() => saveLesson(mi, li)}><Save className="h-4 w-4" /></Button>
                                <Button type="button" variant="destructive" size="sm" onClick={() => lesson.id && deleteLessonMutation.mutate(lesson.id)}><Trash2 className="h-4 w-4" /></Button>
                              </div>
                            ))}
                            <Button type="button" variant="ghost" size="sm" onClick={() => addLesson(mi)} className="w-full"><Plus className="h-4 w-4 me-2" /> Add Lesson</Button>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Settings Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader><CardTitle>Settings</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Price *</label>
                  <Input type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })} required />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Currency</label>
                  <select className="w-full px-3 py-2 border rounded-md" value={formData.currency} onChange={(e) => setFormData({ ...formData, currency: e.target.value })}>
                    <option value="IQD">IQD</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Duration (Hours)</label>
                  <Input type="number" value={formData.durationHours} onChange={(e) => setFormData({ ...formData, durationHours: Number(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Level</label>
                  <select className="w-full px-3 py-2 border rounded-md" value={formData.level} onChange={(e) => setFormData({ ...formData, level: e.target.value })}>
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Thumbnail URL</label>
                  <Input value={formData.thumbnailUrl} onChange={(e) => setFormData({ ...formData, thumbnailUrl: e.target.value })} placeholder="https://example.com/image.jpg" />
                  {formData.thumbnailUrl && <img src={formData.thumbnailUrl} alt="Preview" className="w-32 h-20 object-cover rounded border" />}
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="isPublished" checked={formData.isPublished} onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })} />
                  <label htmlFor="isPublished" className="text-sm">Published</label>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="isFeatured" checked={formData.isFeatured} onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })} />
                  <label htmlFor="isFeatured" className="text-sm">Featured</label>
                </div>

                {/* Access Control */}
                <div className="border-t pt-4 mt-4 space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700">Access Control</h3>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Access Mode</label>
                    <select
                      className="w-full px-3 py-2 border rounded-md"
                      value={formData.accessMode}
                      onChange={(e) => setFormData({ ...formData, accessMode: e.target.value })}
                    >
                      <option value="public">Public - Anyone can access</option>
                      <option value="restricted">Restricted - Whitelisted emails only</option>
                    </select>
                  </div>
                  {formData.accessMode === 'restricted' && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Allowed Emails ({(() => { try { return JSON.parse(formData.allowedEmails || '[]').length; } catch { return 0; } })()})
                      </label>
                      <textarea
                        className="w-full px-3 py-2 border rounded-md text-sm font-mono"
                        rows={5}
                        placeholder="Enter one email per line&#10;e.g.&#10;student1@gmail.com&#10;student2@gmail.com"
                        value={(() => { try { return JSON.parse(formData.allowedEmails || '[]').join('\n'); } catch { return ''; } })()}
                        onChange={(e) => {
                          const emails = e.target.value.split('\n').map(em => em.trim()).filter(em => em);
                          setFormData({ ...formData, allowedEmails: JSON.stringify(emails) });
                        }}
                      />
                      <p className="text-xs text-gray-500">One email per line. Only these users will see and access this course.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            <Button type="submit" className="w-full" disabled={saveMutation.isPending}>
              <Save className="h-4 w-4 me-2" />{saveMutation.isPending ? 'Saving...' : 'Save Course'}
            </Button>
          </div>
        </div>
      </form>

      {/* Quiz Editor Modal */}
      {editingQuiz && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-auto">
            <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-bold">Quiz Editor</h2>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditingQuiz(null)}>Cancel</Button>
                <Button onClick={saveQuiz}><Save className="h-4 w-4 me-2" /> Save Quiz</Button>
              </div>
            </div>
            <div className="p-4 space-y-4">
              <div className="border p-3 rounded bg-blue-50 text-sm text-blue-700">
                Pass mark is fixed at 65% (matching ITIL Foundation exam standard)
              </div>

              {quizQuestions.map((q, qi) => (
                <div key={q.id || qi} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <span className="font-medium text-gray-500">Q{qi + 1}</span>
                    <div className="flex-1 space-y-2">
                      <Input value={q.questionEn} onChange={(e) => updateQuestion(qi, 'questionEn', e.target.value)} placeholder="Question" />
                    </div>
                    <Button variant="destructive" size="sm" onClick={() => deleteQuestion(qi)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <div className="ms-8 space-y-2">
                    <p className="text-sm font-medium text-gray-600">Answers:</p>
                    {q.answers.map((a, ai) => (
                      <div key={ai} className="flex items-center gap-2">
                        <button type="button" onClick={() => updateAnswer(qi, ai, 'isCorrect', true)} className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${a.isCorrect ? 'border-green-500 bg-green-500 text-white' : 'border-gray-300'}`}>
                          {a.isCorrect && <Check className="h-4 w-4" />}
                        </button>
                        <Input value={a.answerEn} onChange={(e) => updateAnswer(qi, ai, 'answerEn', e.target.value)} placeholder="Answer" className="flex-1" />
                        <Button variant="ghost" size="sm" onClick={() => {
                          const updated = [...quizQuestions];
                          updated[qi].answers = updated[qi].answers.filter((_, i) => i !== ai);
                          setQuizQuestions(updated);
                        }}><X className="h-4 w-4" /></Button>
                      </div>
                    ))}
                    <Button type="button" variant="ghost" size="sm" onClick={() => addAnswer(qi)}><Plus className="h-4 w-4 me-1" /> Add Answer</Button>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addQuestion} className="w-full"><Plus className="h-4 w-4 me-2" /> Add Question</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
